import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check, Download, Code2, Image as ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROMO_URL = `${API}/promo.png`;

const VARIANTS = [
  { key: "dark", label: "Dark", w: 280, h: 72 },
  { key: "light", label: "Light", w: 280, h: 72 },
];

const CopyBlock = ({ code, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <div className="relative">
      <pre className="font-mono text-xs text-[#E6E2D6]/80 bg-[#020f14] border border-[#485354]/40 p-5 pr-14 overflow-x-auto whitespace-pre-wrap break-all" data-testid={`${testid}-code`}>{code}</pre>
      <button
        onClick={copy}
        data-testid={`${testid}-copy`}
        className="absolute top-3 right-3 p-2 border border-[#485354]/50 text-[#485354] hover:text-[#70250a] hover:border-[#70250a] transition-colors"
        aria-label="Copy code"
      >
        {copied ? <Check className="w-4 h-4 text-[#6d411c]" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
};

export const Badges = () => {
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const downloadPng = async (v) => {
    try {
      const res = await fetch(`${API}/badge/${v.key}.svg`);
      const svgText = await res.text();
      const scale = 3;
      const img = new Image();
      const blobUrl = URL.createObjectURL(new Blob([svgText], { type: "image/svg+xml" }));
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = v.w * scale;
        canvas.height = v.h * scale;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        URL.revokeObjectURL(blobUrl);
        canvas.toBlob((b) => {
          const a = document.createElement("a");
          a.href = URL.createObjectURL(b);
          a.download = `inkshroud-badge-${v.key}.png`;
          a.click();
          URL.revokeObjectURL(a.href);
          toast.success(`Downloaded ${v.label} badge (PNG)`);
        }, "image/png");
      };
      img.src = blobUrl;
    } catch {
      toast.error("Could not generate PNG");
    }
  };

  const badgeSnippet = `<a href="${origin}" target="_blank" rel="noopener noreferrer">\n  <img src="${origin}/api/badge/dark.svg"\n    alt="Protected by Inkshroud" width="280" height="72" />\n</a>`;

  return (
    <section id="badges" className="relative max-w-6xl mx-auto px-6 py-20 md:py-28 border-t border-[#485354]/25 scroll-mt-20">
      <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#70250a] mb-4">Spread the shroud</p>
      <h2 className="font-cinzel font-bold text-3xl sm:text-4xl text-[#E6E2D6] uppercase tracking-tight mb-4">Badges &amp; embeds</h2>
      <p className="font-lora text-[#E6E2D6]/60 max-w-2xl mb-14">Add the tool to your own site, wear the mark of protection, and grab a graphic for your advertising.</p>

      {/* Badges */}
      <div className="mb-20">
        <h3 className="font-cinzel text-xl text-[#6d411c] mb-6">1 · The protection badge</h3>
        <div className="grid sm:grid-cols-2 gap-6 mb-8 max-w-2xl">
          {VARIANTS.map((v) => (
            <div key={v.key} className="border border-[#485354]/40 bg-[#041b24]/60 p-6 flex flex-col items-center gap-5" data-testid={`badge-card-${v.key}`}>
              <div className="flex-1 flex items-center justify-center min-h-[90px] w-full">
                <img src={`${API}/badge/${v.key}.svg`} alt={`Inkshroud ${v.label} badge`} style={{ maxWidth: "100%" }} data-testid={`badge-preview-${v.key}`} />
              </div>
              <div className="flex items-center gap-2 w-full">
                <a
                  href={`${API}/badge/${v.key}.svg`} download={`inkshroud-badge-${v.key}.svg`}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-[#485354] text-[#E6E2D6] font-mono text-xs uppercase tracking-widest py-3 hover:border-[#6d411c] hover:bg-[#041b24] transition-colors"
                  data-testid={`badge-svg-${v.key}`}
                >
                  <Download className="w-3.5 h-3.5" /> svg
                </a>
                <button
                  onClick={() => downloadPng(v)}
                  className="flex-1 inline-flex items-center justify-center gap-2 border border-[#485354] text-[#E6E2D6] font-mono text-xs uppercase tracking-widest py-3 hover:border-[#6d411c] hover:bg-[#041b24] transition-colors"
                  data-testid={`badge-png-${v.key}`}
                >
                  <Download className="w-3.5 h-3.5" /> png
                </button>
              </div>
            </div>
          ))}
        </div>
        <p className="font-lora text-sm text-[#E6E2D6]/50 mb-3 flex items-center gap-2"><Code2 className="w-4 h-4 text-[#70250a]" /> Paste this HTML to link the badge back to Inkshroud:</p>
        <CopyBlock code={badgeSnippet} testid="badge-embed" />
      </div>

      {/* Promo graphic */}
      <div>
        <h3 className="font-cinzel text-xl text-[#6d411c] mb-6">2 · Advertising graphic</h3>
        <div className="grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2 border border-[#485354]/40 bg-[#020f14] overflow-hidden">
            <img src={PROMO_URL} alt="Inkshroud promotional graphic" className="w-full h-auto block" data-testid="promo-graphic" />
          </div>
          <div>
            <p className="font-lora text-sm text-[#E6E2D6]/60 mb-6 leading-relaxed flex items-start gap-2">
              <ImageIcon className="w-4 h-4 text-[#70250a] mt-1 shrink-0" />
              A ready-made graphic for your website banners, newsletter, or social ads.
            </p>
            <a
              href={PROMO_URL} download="inkshroud-promo.png" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#70250a] text-[#E6E2D6] font-cinzel font-bold uppercase tracking-widest text-sm py-4 px-8 hover:bg-[#8A2E0D] transition-colors duration-300"
              data-testid="download-promo"
            >
              <Download className="w-4 h-4" /> Download graphic
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
